/**
 * Persist lightweight CRM session heartbeats for CEO time-costing.
 * Auth staff only; no keystroke/content logging. Not written to Google Calendar.
 */
import {
  addDoc,
  collection,
  getDocs,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import type { CrmActivityClass, CrmActivityEntityType, CrmSessionHeartbeat } from '../types';
import { isIgnoredCrmSessionHeartbeatError } from './crmSessionHeartbeatErrors';
import { db } from './firebase';

export const CRM_SESSION_HEARTBEATS = 'crmSessionHeartbeats';

function toDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return new Date();
}

function toTimestamp(value: Date | Timestamp): Timestamp {
  return value instanceof Date ? Timestamp.fromDate(value) : value;
}

export function toFirestoreCrmSessionHeartbeat(
  heartbeat: Omit<CrmSessionHeartbeat, 'id'>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    userId: heartbeat.userId,
    userEmail: heartbeat.userEmail,
    userName: heartbeat.userName,
    timestamp: toTimestamp(heartbeat.timestamp),
    path: heartbeat.path,
    pageKey: heartbeat.pageKey,
    pageLabel: heartbeat.pageLabel,
    activityClass: heartbeat.activityClass,
    jobId: heartbeat.jobId ?? null,
    dealId: heartbeat.dealId ?? null,
    entityType: heartbeat.entityType ?? null,
    entityId: heartbeat.entityId ?? null,
    entityName: heartbeat.entityName ?? null,
    focused: heartbeat.focused === true,
    visible: heartbeat.visible === true,
    createdAt: toTimestamp(heartbeat.createdAt),
  };
  if (heartbeat.moduleKey) payload.moduleKey = heartbeat.moduleKey;
  if (heartbeat.moduleLabel) payload.moduleLabel = heartbeat.moduleLabel;
  if (heartbeat.viewKey) payload.viewKey = heartbeat.viewKey;
  if (heartbeat.viewLabel) payload.viewLabel = heartbeat.viewLabel;
  if (heartbeat.sessionId) payload.sessionId = heartbeat.sessionId;
  if (typeof heartbeat.lastInputAgeSec === 'number') payload.lastInputAgeSec = heartbeat.lastInputAgeSec;
  if (heartbeat.idleState) payload.idleState = heartbeat.idleState;
  return payload;
}

export function fromFirestoreCrmSessionHeartbeat(
  id: string,
  data: Record<string, unknown>,
): CrmSessionHeartbeat {
  const activityClass = (typeof data.activityClass === 'string'
    ? data.activityClass
    : 'unknown') as CrmActivityClass;
  return {
    id,
    userId: typeof data.userId === 'string' ? data.userId : '',
    userEmail: typeof data.userEmail === 'string' ? data.userEmail : '',
    userName: typeof data.userName === 'string' ? data.userName : 'Unknown',
    timestamp: toDate(data.timestamp),
    path: typeof data.path === 'string' ? data.path : '/',
    pageKey: typeof data.pageKey === 'string' ? data.pageKey : 'other',
    pageLabel: typeof data.pageLabel === 'string' ? data.pageLabel : 'CRM',
    activityClass,
    jobId: typeof data.jobId === 'string' ? data.jobId : undefined,
    dealId: typeof data.dealId === 'string' ? data.dealId : undefined,
    entityType: (data.entityType as CrmActivityEntityType | undefined) || undefined,
    entityId: typeof data.entityId === 'string' ? data.entityId : undefined,
    entityName: typeof data.entityName === 'string' ? data.entityName : undefined,
    moduleKey: typeof data.moduleKey === 'string' ? data.moduleKey : undefined,
    moduleLabel: typeof data.moduleLabel === 'string' ? data.moduleLabel : undefined,
    viewKey: typeof data.viewKey === 'string' ? data.viewKey : undefined,
    viewLabel: typeof data.viewLabel === 'string' ? data.viewLabel : undefined,
    sessionId: typeof data.sessionId === 'string' ? data.sessionId : undefined,
    lastInputAgeSec: typeof data.lastInputAgeSec === 'number' ? data.lastInputAgeSec : undefined,
    idleState: (data.idleState as CrmSessionHeartbeat['idleState']) || undefined,
    focused: data.focused === true,
    visible: data.visible !== false,
    createdAt: toDate(data.createdAt),
  };
}

export async function saveCrmSessionHeartbeat(
  heartbeat: Omit<CrmSessionHeartbeat, 'id'>,
): Promise<string> {
  if (!heartbeat.userId) return '';
  // Ignore noise: background / unfocused tabs.
  if (!heartbeat.visible || !heartbeat.focused) return '';
  try {
    const ref = await addDoc(
      collection(db, CRM_SESSION_HEARTBEATS),
      toFirestoreCrmSessionHeartbeat(heartbeat),
    );
    return ref.id;
  } catch (err) {
    // addDoc picks the id before the write. A retry after the server already
    // stored it comes back as already-exists; the heartbeat is saved.
    if (isIgnoredCrmSessionHeartbeatError(err)) return '';
    throw err;
  }
}

export async function fetchCrmSessionHeartbeatsInRange(options: {
  start: Date;
  end: Date;
  userId?: string;
}): Promise<CrmSessionHeartbeat[]> {
  const startTs = Timestamp.fromDate(options.start);
  const endTs = Timestamp.fromDate(options.end);
  const col = collection(db, CRM_SESSION_HEARTBEATS);

  const q = options.userId
    ? query(
      col,
      where('userId', '==', options.userId),
      where('timestamp', '>=', startTs),
      where('timestamp', '<=', endTs),
    )
    : query(
      col,
      where('timestamp', '>=', startTs),
      where('timestamp', '<=', endTs),
    );

  const snap = await getDocs(q);
  return snap.docs
    .map((d) => fromFirestoreCrmSessionHeartbeat(d.id, d.data() as Record<string, unknown>))
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}
